
import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Node } from '@xyflow/react';
import { fetchComponents } from '@/utils/services/componentService';
import { createComponent } from '@/utils/componentService';
import { Component } from '@/utils/types/componentTypes';
import { NodeData } from './initial-nodes';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/context/OrganizationContext';

interface NodePropertiesPanelProps {
  node: Node<NodeData> | null;
  onClose: () => void;
  onSave: (nodeId: string, title: string, selectedComponentId: string, selectedComponentName: string, selectedOrganizationId: string, selectedOrganizationName: string, codeChanges7Days?: number, codeChanges30Days?: number, deploys7Days?: number) => void;
}

export function NodePropertiesPanel({ node, onClose, onSave }: NodePropertiesPanelProps) {
  const { organizations } = useOrganization();
  const [title, setTitle] = useState('');
  const [selectedComponentId, setSelectedComponentId] = useState('novo');
  const [selectedComponentName, setSelectedComponentName] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedOrganizationName, setSelectedOrganizationName] = useState('');
  const [components, setComponents] = useState<Component[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [titleError, setTitleError] = useState('');

  useEffect(() => {
    if (node) {
      setTitle(node.data.title || '');
      setSelectedComponentName(node.data.selectedComponentName || '');
      setSelectedOrganizationId(node.data.selectedOrganizationId || '');
      setSelectedOrganizationName(node.data.selectedOrganizationName || '');
      
      // Se o nó tem uma organização, carregue os componentes dessa organização
      if (node.data.selectedOrganizationId && (node.data as any).componentTypeId) {
        loadComponents((node.data as any).componentTypeId as string, node.data.selectedOrganizationId, node.data.selectedComponentId);
      }
    }
  }, [node]);

  // Carrega componentes quando a organização ou tipo de componente mudam
  useEffect(() => {
    if (selectedOrganizationId && (node?.data as any)?.componentTypeId) {
      loadComponents((node.data as any).componentTypeId as string, selectedOrganizationId);
    }
  }, [selectedOrganizationId, (node?.data as any)?.componentTypeId]);

  const loadComponents = async (componentTypeId: string, organizationId: string, nodeSelectedComponentId?: string) => {
    if (!organizationId || !componentTypeId) return;
    
    setIsLoading(true);
    try {
      console.log('Buscando componentes para organização:', organizationId);
      const fetchedComponents = await fetchComponents(
        organizationId,
        'name',
        'asc',
        '',
        componentTypeId
      );
      console.log('Componentes encontrados:', fetchedComponents);
      setComponents(fetchedComponents);
      
      // Definir o componente selecionado após carregar a lista
      if (nodeSelectedComponentId && fetchedComponents.some(comp => comp.id === nodeSelectedComponentId)) {
        setSelectedComponentId(nodeSelectedComponentId);
      } else {
        setSelectedComponentId('novo');
      }
    } catch (error) {
      console.error('Error loading components:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedOrganizationId) {
      return;
    }

    if (selectedComponentId === 'novo') {
      if (!title.trim()) {
        setTitleError('O título é obrigatório para novos componentes');
        return;
      }

      if (node && (node.data as any).componentTypeId) {
        const result = await createComponent(
          title,
          (node.data as any).componentTypeId as string,
          selectedOrganizationId,
          '',
          ''
        );

        if (result.success && result.component) {
          onSave(node.id, title, result.component.id, title, selectedOrganizationId, selectedOrganizationName);
        }
      }
    } else if (node) {
      // Buscar os dados das novas colunas quando um componente existente é selecionado
      let codeChanges7Days = 0;
      let codeChanges30Days = 0;
      let deploys7Days = 0;

      try {
        const { data: componentData, error: componentError } = await supabase
          .from('components')
          .select('id, name, code_changes_7days, code_changes_30days, deploys_7days')
          .eq('id', selectedComponentId)
          .single();

        if (!componentError && componentData) {
          codeChanges7Days = (componentData as any).code_changes_7days || 0;
          codeChanges30Days = (componentData as any).code_changes_30days || 0;
          deploys7Days = (componentData as any).deploys_7days || 0;
        }
      } catch (error) {
        console.error('Error fetching component code changes:', error);
      }

      onSave(node.id, title, selectedComponentId, selectedComponentName, selectedOrganizationId, selectedOrganizationName, codeChanges7Days, codeChanges30Days, deploys7Days);
    }
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.slice(0, 30);
    setTitle(value);
    if (selectedComponentId === 'novo' && !value.trim()) {
      setTitleError('O título é obrigatório para novos componentes');
    } else {
      setTitleError('');
    }
  };

  const handleOrganizationChange = (value: string) => {
    setSelectedOrganizationId(value);
    const selectedOrg = organizations.find(org => org.id === value);
    setSelectedOrganizationName(selectedOrg?.name || '');
    
    // Reset component selection when organization changes
    setSelectedComponentId('novo');
    setSelectedComponentName('');
    setComponents([]);
  };

  const handleComponentChange = (value: string) => {
    setSelectedComponentId(value);
    
    if (value !== 'novo') {
      const selectedComponent = components.find(comp => comp.id === value);
      setSelectedComponentName(selectedComponent?.name || '');
      setTitle('');
      setTitleError('');
    }
  };

  if (!node) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium">Propriedades do Nó</h3>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="h-4 w-4" />
          <span className="sr-only">Fechar</span>
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="flex-1 overflow-auto p-4 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="node-uuid">UUID</Label>
          <Input 
            id="node-uuid" 
            value={node.data.uuid || ''} 
            readOnly 
            disabled 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="component-name">Tipo de Componente</Label>
          <Input 
            id="component-name" 
            value={(node.data as any).name as string || ''} 
            readOnly 
            disabled 
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="organization-select">Organização <span className="text-red-500">*</span></Label>
          <Select 
            value={selectedOrganizationId} 
            onValueChange={handleOrganizationChange}
          >
            <SelectTrigger id="organization-select">
              <SelectValue placeholder="Selecione uma organização" />
            </SelectTrigger>
            <SelectContent>
              {organizations.map((organization) => (
                <SelectItem key={organization.id} value={organization.id}>
                  {organization.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="component-select">Componente</Label>
          <Select 
            value={selectedComponentId} 
            onValueChange={handleComponentChange}
            disabled={isLoading || !selectedOrganizationId}
          >
            <SelectTrigger id="component-select">
              <SelectValue placeholder="Selecione um componente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="novo">-- Novo Componente --</SelectItem>
              {components.map((component) => (
                <SelectItem key={component.id} value={component.id}>
                  {component.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {selectedComponentId === 'novo' && (
          <div className="space-y-2">
            <Label htmlFor="node-title">Título <span className="text-red-500">*</span></Label>
            <Input 
              id="node-title" 
              name="title"
              value={title}
              onChange={handleTitleChange}
              maxLength={30}
            />
            {titleError && <p className="text-sm text-red-500">{titleError}</p>}
          </div>
        )}
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            type="submit" 
            className="bg-[#05445e] hover:bg-[#189ab4]"
            disabled={!selectedOrganizationId || (selectedComponentId === 'novo' && !title.trim())}
          >
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
}
